import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { EmailLink } from '@/components/ui/TextLink';
import { api } from '@/lib/api';
import {
  CONTACT_NOT_ON_COMPANY,
  contactScopeIsAuthoritative,
  primaryContactSeed,
  type ContactScope,
} from '@/lib/contact-scope';
import { EMPTY_SIGNER, type SignerValue } from '@/lib/docusign';
import { Button, Combobox, Field, Input, type ComboboxOption } from '@/components/ui';

/*
 * WHO SIGNS — a contact picker scoped to the document's own company.
 *
 * ── IT REUSES `@/lib/contact-scope`, AND THAT IS THE POINT ───────────────
 *
 * "Which contacts belong to this company, and which one is primary" is already
 * answered once, for `DealFormModal`, `QuoteFormModal` and `InvoiceFormModal`
 * (owner's decision, 2026-08-29). Every sentence this field says about an empty,
 * failed or unscoped list comes from `contactScopeHint`, the "not one of this
 * company's contacts" label comes from `CONTACT_NOT_ON_COMPANY`, and the seed
 * comes from `primaryContactSeed`. A second implementation is precisely what that
 * module exists to prevent — and what would drift is copy, which nothing
 * typechecks.
 *
 * ── WHAT IS GENUINELY DIFFERENT HERE, AND WHY IT IS NOT A SECOND COPY ────
 *
 * A document's Contact field records who the counterparty is. **This field
 * chooses who receives a contract.** Two consequences that the three form modals
 * do not have and must not inherit:
 *
 *  1. **The EMAIL is the payload, not the id.** `signer.email` reaches DocuSign
 *     as a string; there is no contact id anywhere in either send schema. Picking
 *     a contact fills BOTH halves of `{name, email}` — the wire shape is
 *     unchanged, the picker is only a better way to fill it.
 *  2. **A contact with no email CANNOT be a signer**, and that is the common case
 *     rather than the edge — only ~16% of the imported Xero contacts carry one.
 *     See {@link SIGNER_NEEDS_EMAIL}.
 *
 * ── WHY THE PICKER IS NOT THE ONLY PATH ──────────────────────────────────
 *
 * The person who signs is sometimes not on file — a procurement officer looped in
 * for one contract, a dean's assistant, somebody at a parent institution. So
 * "Someone else" is a first-class second path, not a fallback hidden behind a
 * failure.
 *
 * ⚠️ **It is a MODE, not an inline "+ Add" like `PickerField`'s.** That was
 * considered and rejected on `contact-scope.ts`'s own reasoning, restated because
 * the two look interchangeable: the deal pickers extend PICKLIST masters, where a
 * missing row is a one-word omission a salesperson can legitimately fix. A contact
 * is a person with an email, an owner and a lead source, and inventing one from a
 * two-field row is how a directory fills with half-records. Sending a contract to
 * somebody does not make them a CRM contact, and this form must not decide that it
 * does.
 *
 * ── CONTACTS WITH NO EMAIL ARE EXCLUDED, AND THEN ACCOUNTED FOR ──────────
 *
 * They are NOT offered as disabled-looking options that refuse on submit: an
 * affordance whose only outcome is a refusal is the thing this codebase removes
 * (the Xero `sync` button, DocuSign's absent connect control). They are NOT
 * silently dropped either — a colleague missing from a list reads as a colleague
 * who is not on file, which sends somebody to create a duplicate. They are left
 * out and COUNTED, with the count naming both fixes: an address on the contact
 * record, or the "Someone else" path right below it.
 *
 * ── ONE ROW OF SEVERAL, SINCE 2026-09-01 ─────────────────────────────────
 *
 * An envelope may carry up to four signers (owner's decision — see
 * `MAX_SIGNERS` in `@/lib/docusign`), and **each is independently a contact or a
 * hand-typed person**, so a form renders one of these per signer and each keeps
 * its own mode, its own picker and its own contact read.
 *
 * Three props exist only for that case and every one is absent or defaulted on a
 * lone signer, so **the single-signer form renders exactly as it did**:
 * {@link SignerFieldProps.ordinal} names the row,
 * {@link SignerFieldProps.action} carries its Remove control, and
 * {@link SignerFieldProps.seedPrimaryContact} is what stops four rows all
 * filling themselves in with the same person.
 */

/*
 * ⚠️ **`SignerValue` and `EMPTY_SIGNER` live in `@/lib/docusign`, not here.**
 * Three components pass the shape around — this field, `ChangeSignerDialog` and
 * `EnvelopePanel` between them — so it belongs in the module that already owns
 * this integration's model rather than in the one component that renders it.
 * Exporting a value from a component file is also the only thing in `src/` that
 * breaks fast refresh.
 */

export interface SignerFieldProps {
  /**
   * The company whose contacts are offered. `null` when the document names none,
   * which offers NOTHING and says why — never the unscoped directory of every
   * contact in the CRM. See `CONTACT_NEEDS_COMPANY`.
   */
  companyId: number | null;
  value: SignerValue;
  onChange: (next: SignerValue) => void;
  /**
   * Already unpacked from the server's keys. A form holding several signers
   * reads them off `signers.{index}.name` / `.email` — see `signerErrorKey()` in
   * `@/lib/docusign` — and hands each row its own pair.
   */
  errors?: { name?: string; email?: string };
  disabled?: boolean;
  /**
   * Which signer this is, 1-based, when the form holds MORE THAN ONE.
   *
   * ⚠️ **Absent means "the only signer", and that case must render exactly as it
   * did before multiple signers existed** — heading "Who signs", control label
   * "Signer". Passing `1` on a lone signer would number a list of one.
   *
   * When it is given, the ordinal becomes the group heading and the control's
   * own label is kept for assistive tech but hidden (`hideLabel`): two visible
   * "Signer 2"s one above the other is the same fact twice, while dropping the
   * label outright would leave four identically-labelled comboboxes on one form.
   */
  ordinal?: number;
  /**
   * A control belonging to this row — in practice Remove. Rendered at the end of
   * the mode-toggle line so it sits with the row it acts on rather than floating
   * under the field it would delete.
   */
  action?: ReactNode;
  /**
   * Fill the company's designated primary contact into an untouched field.
   * Defaults to `true`, which is what a single signer has always done.
   *
   * 🔴 **A form holding several signers MUST pass `false` on every row but the
   * first.** The seed is per-instance — each field owns its own "have I seeded
   * yet" ref — so four rows would each fill in the SAME primary contact, and an
   * envelope carrying one person four times is refused by DocuSign, which tells
   * recipients apart by name and email. It would not look like a bug: the rows
   * fill themselves in with a plausible name.
   */
  seedPrimaryContact?: boolean;
}

/** A contact that can actually be sent a contract: it has an address. */
interface SignerOption {
  id: number;
  name: string;
  email: string;
}

interface Loaded {
  /** The company these rows answer for, so a stale list is dropped, not dimmed. */
  companyId: number;
  options: SignerOption[];
  /** Contacts at this company with no email address. Reported, never offered. */
  withoutEmail: number;
  /** True when the request came back an error — NOT the same as empty. */
  failed: boolean;
  /** More than one page: the picker is showing a prefix of the directory. */
  truncated: boolean;
  /** The company's designated primary, only when they can actually sign. */
  seed: SignerOption | null;
  /** The primary exists but has no email, so they were not seeded. */
  primaryUnusable: boolean;
}

export function SignerField({
  companyId,
  value,
  onChange,
  errors,
  disabled = false,
  ordinal,
  action,
  seedPrimaryContact = true,
}: SignerFieldProps) {
  /*
   * One name for this signer, used for the heading, the picker's label and both
   * hand-typed boxes, so a row cannot come to call itself two things. Absent
   * `ordinal` reproduces the single-signer form word for word.
   */
  const label = ordinal === undefined ? 'Signer' : `Signer ${ordinal}`;
  const heading = ordinal === undefined ? 'Who signs' : label;
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [term, setTerm] = useState('');
  const [loadedTerm, setLoadedTerm] = useState<string | null>(null);

  /*
   * Typing an address by hand rather than choosing a contact.
   *
   * Seeded from the value so a signer handed in by "Change signer and resend"
   * lands in the mode that can express it: a hand-typed address has no
   * `contactId`, and starting such a value in the picker would show an empty
   * picker above two filled boxes.
   */
  const [manual, setManual] = useState(
    () => value.contactId === null && (value.name !== '' || value.email !== ''),
  );

  /*
   * The seed fires ONCE per company, and only into an untouched field.
   *
   * ⚠️ Tracked explicitly rather than inferred from the field being empty — the
   * flag is set even when somebody CLEARS the seeded signer. Without that,
   * clearing hands control back to the seeder, which refills on the next render
   * and the field fights the user. Same rule as the seeded document name.
   */
  const seededFor = useRef<number | null>(null);

  useEffect(() => {
    if (companyId === null) {
      setLoaded(null);
      setLoadedTerm(null);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    /*
     * Two reads, and they answer different questions.
     *
     *   /contacts?companyId=  WHO may be offered. A real server-side filter, so
     *                         the list is SCOPED rather than narrowed locally —
     *                         a contact at another account is never offered, at
     *                         any page size. ⚠️ A SINGLE id; `?companyId=15,16`
     *                         is a 422.
     *   /companies/{id}       WHICH of them is primary, and their address. The
     *                         contact rows do not carry the designation, so
     *                         there is nothing to derive it from.
     *
     * The company read is allowed to fail on its own: `primaryContact` is a
     * SEED, and a form that opens with an empty picker is workable while one
     * that refuses to load is not.
     */
    const contacts = api.listContacts(
      { search: term, companyId, perPage: 100, sort: 'name', order: 'asc' },
      signal,
    );
    const company = api
      .getCompany(companyId, signal)
      .then((result) => primaryContactSeed(result.data))
      .catch(() => null);

    Promise.all([contacts, company])
      .then(([list, primary]) => {
        if (signal.aborted) return;
        const options: SignerOption[] = [];
        let withoutEmail = 0;
        for (const row of list.data) {
          const email = row.email?.trim() ?? '';
          if (email === '') {
            withoutEmail += 1;
            continue;
          }
          options.push({ id: row.id, name: row.name, email });
        }
        /*
         * The primary's address comes from the SCOPED LIST where it is there,
         * because `primaryContactSeed` deliberately returns `{id, name}` only —
         * it is the picker's seed for three forms that never needed an email.
         * Falling back to the company read's own `email` covers the case the
         * list cannot: a designation the list did not return, which the company
         * read resolves anyway because it joins on the contact id alone.
         */
        const primaryRow = primary
          ? (options.find((option) => option.id === primary.id) ?? null)
          : null;
        setLoaded({
          companyId,
          options,
          withoutEmail,
          failed: false,
          truncated: list.meta.totalPages > 1,
          seed: primaryRow,
          /*
           * ⚠️ A primary who cannot sign is a STATE, not a missing seed. Said
           * out loud, because an unexplained empty picker on a company that
           * plainly has a primary contact reads as the seed being broken.
           */
          primaryUnusable: primary !== null && primaryRow === null,
        });
      })
      .catch(() => {
        /*
         * ⚠️ `failed`, not merely empty. Both render no options, and telling
         * somebody a company has no contacts when the request simply did not
         * come back sends them to create a person who already exists.
         */
        if (!signal.aborted) {
          setLoaded({
            companyId,
            options: [],
            withoutEmail: 0,
            failed: true,
            truncated: false,
            seed: null,
            primaryUnusable: false,
          });
        }
      })
      .finally(() => {
        if (!signal.aborted) setLoadedTerm(term);
      });

    return () => controller.abort();
  }, [companyId, term]);

  /* Rows only count while they answer for the company currently selected. */
  const answered = loaded !== null && loaded.companyId === companyId ? loaded : null;
  const loading = companyId !== null && (answered === null || loadedTerm !== term);

  /* Seed the primary contact, once, into a field nobody has touched. */
  useEffect(() => {
    /*
     * ⚠️ Checked FIRST, and before `seededFor` is stamped. A second, third or
     * fourth signer row must not be filled with the person the first row already
     * holds — see {@link SignerFieldProps.seedPrimaryContact}.
     */
    if (!seedPrimaryContact) return;
    if (companyId === null || answered === null || answered.seed === null) return;
    if (seededFor.current === companyId) return;
    seededFor.current = companyId;
    if (value.contactId !== null || value.name !== '' || value.email !== '') return;
    onChange({
      contactId: answered.seed.id,
      name: answered.seed.name,
      email: answered.seed.email,
    });
    // `value` and `onChange` are deliberately out of the dependency list: this
    // must fire on the COMPANY changing, never on the field being edited.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, answered, seedPrimaryContact]);

  /*
   * The scope, in the one shape `@/lib/contact-scope` words hints from.
   *
   * ⚠️ **`count` is the SIGNABLE count, not the row count**, and that is a real
   * decision rather than a convenience. A company whose every contact lacks an
   * address has contacts, so `CONTACT_SCOPE_EMPTY`'s "add one on the company
   * record first" would be wrong advice — the fix is an address, not a person.
   * That case is covered by {@link contactsWithoutEmailNote} instead, so the
   * count here is raised by the excluded rows to keep the empty sentence off.
   */
  const scope: ContactScope = {
    companyId,
    loading,
    failed: answered?.failed ?? false,
    count: (answered?.options.length ?? 0) + (answered?.withoutEmail ?? 0),
    searching: term.trim() !== '',
    cleared: false,
  };

  /*
   * ⚠️ Read out as a BOOLEAN before the memo below, never passed as the `scope`
   * object itself. `scope` is rebuilt every render, so listing it as a dependency
   * would make the memo a no-op while still looking like one — the same trap
   * `NO_SCOPE_RESULT` exists to close in the form modals' own lookup hook.
   */
  const authoritative = contactScopeIsAuthoritative(scope);

  const options = useMemo<ComboboxOption[]>(() => {
    const list: ComboboxOption[] = (answered?.options ?? []).map((option) => ({
      value: String(option.id),
      label: option.name,
      // The address, because it is what DocuSign matches on and what
      // disambiguates two people with one name.
      hint: option.email,
    }));
    /*
     * ⚠️ The pin is UNCONDITIONAL; only the "not one of this company's contacts"
     * REASON is gated, on `authoritative`. A stored contact dropped from the list
     * renders as "Unassigned" over a form that holds one — and accusing a contact
     * of belonging to another company on the strength of a request that never
     * came back is a confident wrong answer where saying nothing costs nothing.
     */
    if (value.contactId !== null && !list.some((o) => o.value === String(value.contactId))) {
      list.unshift({
        value: String(value.contactId),
        label: value.name || `Contact #${value.contactId}`,
        hint: authoritative ? `${value.email} · ${CONTACT_NOT_ON_COMPANY}` : value.email,
      });
    }
    return list;
  }, [answered, value.contactId, value.name, value.email, authoritative]);

  function chooseContact(next: string) {
    if (next === '') {
      onChange(EMPTY_SIGNER);
      return;
    }
    const picked = (answered?.options ?? []).find((option) => String(option.id) === next);
    if (!picked) return;
    // BOTH halves in one call. Name and email are one choice here, and a patch
    // that set one alone would leave a signer DocuSign cannot match.
    onChange({ contactId: picked.id, name: picked.name, email: picked.email });
  }

  return (
    <div className="grid min-w-0 gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="m-0 mr-auto text-xs font-medium text-fg-secondary">{heading}</p>
        <Button
          type="button"
          size="sm"
          variant={!manual ? 'secondary' : 'ghost'}
          aria-pressed={!manual}
          disabled={disabled}
          onClick={() => {
            setManual(false);
            // The typed pair is DISCARDED rather than carried into the picker.
            // Leaving it would show a name beside an unselected picker, which
            // reads as a selection that failed to take.
            if (value.contactId === null) onChange(EMPTY_SIGNER);
          }}
        >
          A contact at this company
        </Button>
        <Button
          type="button"
          size="sm"
          variant={manual ? 'secondary' : 'ghost'}
          aria-pressed={manual}
          disabled={disabled}
          onClick={() => {
            setManual(true);
            /*
             * The chosen contact's NAME AND ADDRESS ARE KEPT and only the id is
             * dropped. Somebody switching here is usually correcting one of the
             * two — a personal address for a shared inbox, a formal name for a
             * signature block — and clearing both would make the common case
             * retype what was already right.
             */
            if (value.contactId !== null) onChange({ ...value, contactId: null });
          }}
        >
          Someone else
        </Button>
        {/*
          Remove, when the form holds more than one signer. It belongs on this
          line rather than under the field: a destructive control sitting below
          the box it empties reads as "clear this", and the row it deletes has to
          be the row it is drawn on.
        */}
        {action}
      </div>

      {!manual ? (
        <div className="grid min-w-0 gap-2">
          <Field
            label={label}
            hideLabel={ordinal !== undefined}
            required
            error={errors?.name ?? errors?.email}
          >
            {(fieldProps) => (
              <Combobox
                {...fieldProps}
                value={value.contactId === null ? '' : String(value.contactId)}
                options={options}
                onChange={chooseContact}
                onSearch={setTerm}
                loading={loading}
                disabled={disabled || companyId === null}
                invalid={Boolean(errors?.name ?? errors?.email)}
                placeholder={companyId === null ? 'No company on this quote' : 'Choose a contact…'}
                searchPlaceholder="Search this company’s contacts…"
              />
            )}
          </Field>

          {/*
            The address the envelope will carry, shown rather than hidden behind
            the option's own hint: it is the value DocuSign matches on, and the
            person pressing Send is entitled to read it before it goes.
          */}
          {value.email !== '' && (
            <p className="m-0 text-xs text-fg-muted [overflow-wrap:anywhere]">
              DocuSign will be given{' '}
              <strong>
                <EmailLink address={value.email} tone="muted" />
              </strong>
              .
            </p>
          )}

          {/*
            ⛔ **THREE SENTENCES WERE REMOVED HERE — owner's helper-text ruling,
            2026-09-05. All three explained why a contact somebody expected is
            NOT on offer, and none of the three is one of the five kinds of
            sentence that may stand beside a control.**

            *SUPERSEDED, the Field hint:* `contactScopeHint(scope)` — the picker
            is scoped to this quote's company, could not be read, or was cleared
            by a company change.

            *SUPERSEDED, the popover footnote:* `Showing the first 100 matches —
            keep typing to narrow.` **The list is still capped at 100 and now
            says nothing about it.**

            *SUPERSEDED, the two warning lines:* `contactsWithoutEmailNote(n)`
            (*"N of this company's contacts have no email address, so they are
            not listed…"*) and, gated on `seedPrimaryContact &&
            answered?.primaryUnusable`, *"This company's primary contact has no
            email address, so nobody was filled in. A signer needs an email
            address — DocuSign matches the recipient on it."*

            ⚠️ **THE STATE THEY DESCRIBED IS UNCHANGED: a contact with no email
            address is NOT OFFERED HERE, silently.** An empty picker on a company
            that plainly has contacts now reads as a broken fetch rather than as
            a missing address, and the recovery — add an address on the contact
            record, or press "Someone else" and type one — is no longer named
            anywhere on this screen.

            ⚠️ **`SIGNER_NEEDS_EMAIL` and `contactsWithoutEmailNote` are
            untouched in `@/lib/docusign`** and still have other callers. Do not
            delete them on the strength of this removal.
          */}
        </div>
      ) : (
        <div className="grid min-w-0 gap-4 sm:grid-cols-2">
          <Field label={`${label} name`} required error={errors?.name}>
            {(fieldProps) => (
              <Input
                {...fieldProps}
                value={value.name}
                maxLength={160}
                autoComplete="off"
                invalid={Boolean(errors?.name)}
                disabled={disabled}
                onChange={(event) => onChange({ ...value, contactId: null, name: event.target.value })}
              />
            )}
          </Field>
          <Field label={`${label} email`} required error={errors?.email}>
            {(fieldProps) => (
              <Input
                {...fieldProps}
                type="email"
                value={value.email}
                maxLength={255}
                autoComplete="off"
                invalid={Boolean(errors?.email)}
                disabled={disabled}
                onChange={(event) =>
                  onChange({ ...value, contactId: null, email: event.target.value })
                }
              />
            )}
          </Field>
        </div>
      )}
    </div>
  );
}
